import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useSearchParams, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api, formatApiError } from "@/lib/api";

const IS_TOUCH = typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches;

const LimeCheck = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="10.5" fill="#CCFF00" stroke="#0A0D14" strokeWidth="1.5" />
    <path d="M7 12.5l3.2 3.2L17 9" fill="none" stroke="#0A0D14" strokeWidth="4.6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M7 12.5l3.2 3.2L17 9" fill="none" stroke="#CCFF00" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PillarRing = ({ pct, color, size = 34 }) => {
  const r = (size - 8) / 2;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative shrink-0">
      <svg width={size} height={size} className="-rotate-90 block">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#E4E4E7" strokeWidth="4" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="4"
          strokeDasharray={c} strokeDashoffset={c * (1 - pct / 100)} strokeLinecap="round" />
      </svg>
      <span className="absolute inset-0 grid place-items-center font-extrabold" style={{ color, fontSize: pct >= 100 ? 12 : 9 }}>
        {pct >= 100 ? "✓" : `${pct}%`}
      </span>
    </div>
  );
};

const ProgressRing = ({ pct, size = 24 }) => {
  const r = (size - 6) / 2;
  const c = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}
      className="absolute bottom-1 right-1"
      style={{ background: "rgba(10,13,20,0.78)", borderRadius: "50%" }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="3" />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#CCFF00" strokeWidth="3"
        strokeDasharray={`${(pct / 100) * c} ${c}`} strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`} />
    </svg>
  );
};
import { useAuth } from "@/contexts/AuthContext";
import LessonCelebration from "@/components/LessonCelebration";
import WaiverStep from "@/components/WaiverStep";
import CoinStreakBanner from "@/components/CoinStreakBanner";
import CourseCompleteCelebration from "@/components/CourseCompleteCelebration";
import { CourseJourneyMap } from "@/components/CourseJourneyMap";
import LevelUpCelebration from "@/components/LevelUpCelebration";
import DiamondCelebration from "@/components/DiamondCelebration";
import { levelOf } from "@/lib/levels";
import {
  Lock, Play, CheckCircle, BookOpen, Question, ArrowLeft, ArrowRight, CornersOut, CornersIn,
  DownloadSimple, FileText, Eye, ShieldCheck, Check, X, Heart, ChatCircleText, Warning, ShoppingCartSimple,
  CircleNotch, ArrowClockwise, MagnifyingGlass,
} from "@phosphor-icons/react";

const escRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const markText = (str, q) => {
  if (!q || !str) return str;
  return str.split(new RegExp(`(${escRe(q)})`, "ig")).map((p, i) =>
    p.toLowerCase() === q.toLowerCase()
      ? <mark key={i} style={{ background: "#CCFF00", padding: "0 2px", borderRadius: 3 }}>{p}</mark>
      : p);
};

// Transcript search: find the moment a topic is taught across every lesson's video transcript
// (plus lesson titles) and jump straight to that lesson with the phrase highlighted.
function TranscriptSearchResults({ items, query, onJump }) {
  const q = query.toLowerCase();
  const results = [];
  items.forEach((it, idx) => {
    const tr = it.transcript || "";
    const inTitle = (it.title || "").toLowerCase().includes(q);
    const ti = tr.toLowerCase().indexOf(q);
    if (!inTitle && ti === -1) return;
    let snippet = null;
    if (ti !== -1) {
      const start = Math.max(0, ti - 55);
      const end = Math.min(tr.length, ti + q.length + 95);
      snippet = `${start > 0 ? "…" : ""}${tr.slice(start, end).trim()}${end < tr.length ? "…" : ""}`;
    }
    const count = ti === -1 ? 0 : tr.toLowerCase().split(q).length - 1;
    results.push({ idx, it, snippet, count });
  });
  if (!results.length) {
    return (
      <p className="text-sm text-[#52525B] px-1" data-testid="transcript-search-empty">
        No lesson mentions “{query}” yet. Try a different word.
      </p>
    );
  }
  return (
    <ol className="flex flex-col gap-2 min-w-0 list-none p-0 m-0" data-testid="transcript-search-results">
      {results.map(({ idx, it, snippet, count }) => {
        const locked = !it.unlocked && !it.completed;
        return (
          <li key={idx}>
            <button data-testid={`transcript-result-${idx}`} onClick={() => onJump(idx)} disabled={locked}
              className="w-full text-left rounded-xl border border-[#E4E4E7] bg-white/70 hover:border-[#CCFF00] p-3 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ transition: "border-color .15s" }}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-bold font-display truncate">{markText(it.title || "", query)}</span>
                {count > 0 && (
                  <span className="shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#CCFF00]/40 text-[#3F6212]">
                    {count} mention{count > 1 ? "s" : ""}
                  </span>
                )}
              </div>
              {snippet && <p className="text-xs leading-relaxed text-[#52525B] mt-1 mb-0">{markText(snippet, query)}</p>}
              {locked && <p className="text-[10px] text-[#A1A1AA] mt-1 mb-0">Locked — reach this lesson to open it</p>}
            </button>
          </li>
        );
      })}
    </ol>
  );
}

// Transcript under a lesson video; auto-opens and scrolls to the first highlighted match
// when the student arrives here from a transcript search.
function TranscriptBlock({ text, highlight, lessonId, defaultLang = "en", onSticky = null }) {
  const ref = useRef(null);
  const hit = !!(highlight && text && text.toLowerCase().includes(highlight.toLowerCase()));
  const [open, setOpen] = useState(hit);
  const [lang, setLang] = useState("en");
  const [fr, setFr] = useState(null);
  const [frBusy, setFrBusy] = useState(false);
  const [frErr, setFrErr] = useState("");
  useEffect(() => { setFr(null); setLang("en"); setFrErr(""); }, [lessonId]);
  useEffect(() => {
    if (defaultLang === "fr" && lessonId) showFr();
    // eslint-disable-next-line
  }, [lessonId, defaultLang]);
  const showFr = async () => {
    setFrErr("");
    if (fr) { setLang("fr"); return; }
    setFrBusy(true);
    try {
      const { data } = await api.get(`/learning/lesson-assets/${lessonId}/transcript-fr`);
      setFr(data.transcript_fr);
      setLang("fr");
    } catch { setFrErr("La traduction n'est pas disponible pour le moment — réessayez."); }
    finally { setFrBusy(false); }
  };
  const pill = (active) => ({
    padding: "2px 10px", borderRadius: "999px", fontSize: "11px", fontWeight: 700, cursor: "pointer",
    background: active ? "#0A0D14" : "#FFFFFF", color: active ? "#FFFFFF" : "#52525B",
    border: "1px solid " + (active ? "#0A0D14" : "#C4C8CE"),
  });
  useEffect(() => {
    if (!hit) return;
    setOpen(true);
    const t = setTimeout(() => {
      ref.current?.querySelector("mark")?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlight, text]);
  return (
    <details ref={ref} open={open} onToggle={(e) => setOpen(e.currentTarget.open)}
      className="rounded-xl border border-[#E4E4E7] bg-white/60 px-4 py-3" data-testid="lesson-transcript">
      <summary className="cursor-pointer text-sm font-bold select-none">
        Transcript{hit ? <span className="ml-2 text-[11px] font-normal text-[#4D7C0F]">— “{highlight}” highlighted below</span> : null}
      </summary>
      {lessonId && (
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <button type="button" style={pill(lang === "en")} onClick={() => { setLang("en"); onSticky?.("en"); }} data-testid="transcript-lang-en">English</button>
          <button type="button" style={pill(lang === "fr")} onClick={() => { showFr(); onSticky?.("fr"); }} disabled={frBusy} data-testid="transcript-lang-fr">
            {frBusy ? "Traduction en cours…" : "Français"}
          </button>
          {frErr && <span className="text-[11px] text-[#b45309]" data-testid="transcript-fr-error">{frErr}</span>}
        </div>
      )}
      <p className="text-sm leading-relaxed whitespace-pre-wrap mt-2 mb-0 text-[#3F3F46]">
        {lang === "fr" && fr ? fr : (hit ? markText(text, highlight) : text)}
      </p>
    </details>
  );
}
import { usePillars, pillarChipStyle } from "@/contexts/PillarsContext";
import { useI18n } from "@/contexts/I18nContext";
import { SosButton } from "@/components/SosButton";
import { FeedbackButton } from "@/components/FeedbackButton";
import { ScreenGuard } from "@/components/ScreenGuard";
import { toast } from "sonner";
import { ReadAloudButton } from "@/components/ReadAloudButton";
import { NdaGate } from "@/components/NdaGate";

export default function CoursePlayerPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const viewGrade = searchParams.get("grade"); // staff "view as grade" from the catalog
  const { user, refreshMe } = useAuth();
  const { t, lang } = useI18n();
  const { labelOf, colorOf, introLabel, pillars } = usePillars();
  // Students may rewind but not skip ahead in videos. Instructors/admins preview freely.
  const isStudentViewer = !!user && !["super_admin", "level2_instructor", "level1_instructor"].includes(user.role);
  const isSchoolStaffViewer = !!user && (user.roles || [user.role]).some((r) => ["super_admin", "school_board_admin", "school_admin", "teacher"].includes(r));
  const nav = useNavigate();
  const loc = useLocation();
  const [justPurchased, setJustPurchased] = useState(!!loc.state?.justPurchased);
  const [course, setCourse] = useState(null);
  const [err, setErr] = useState("");
  const [expired, setExpired] = useState(false);
  const [active, setActive] = useState(null);
  const [mobileLesson, setMobileLesson] = useState(false);
  const [journeyView, setJourneyView] = useState(() => {
    try { return localStorage.getItem("courseNavView") || "map"; } catch { return "map"; }
  });
  const [courseComplete, setCourseComplete] = useState(false);
  const completeTimer = useRef(null);
  const [resume, setResume] = useState(undefined); // undefined = not fetched yet
  const [heavyAckedKey, setHeavyAckedKey] = useState(null); // heavy warning: re-shown every time a lesson is opened
  useEffect(() => { setHeavyAckedKey(null); }, [active]);
  const [helpEnabled, setHelpEnabled] = useState(false);
  const [tQuery, setTQuery] = useState("");
  const [jumpTerm, setJumpTerm] = useState("");

  useEffect(() => {
    if (user?.role !== "public_student") {
      api.get("/inbox/summary").then(r => setHelpEnabled(!!r.data?.channels?.per_lesson_help)).catch(() => {});
    }
  }, []);

  // Watched-seconds → % for the curriculum progress bar (started but incomplete).
  const lessonPct = (it) => {
    const pos = resume?.video_positions;
    if (!pos || it.completed) return 0;
    const secs = Object.entries(pos).filter(([k]) => k.startsWith(it.lesson_asset_id + ":"))
      .reduce((s, [, v]) => s + (v || 0), 0);
    if (secs <= 0) return 0;
    return Math.min(99, Math.max(3, Math.round((secs / ((it.duration_minutes || 5) * 60)) * 100)));
  };

  // On return to the course, jump the mobile TOC to where the learner left off.
  // Fresh starts (first lesson) stay at the very top so "Back to dashboard" is visible.
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current || !course || active === null) return;
    restoredRef.current = true;
    if (window.innerWidth >= 1024 || active === 0) return;
    const idx = active;
    // One-shot timer, deliberately not cleared on cleanup (StrictMode-safe).
    setTimeout(() => {
      document.querySelector(`[data-testid="curriculum-item-${idx}"]`)?.scrollIntoView({ block: "center" });
    }, 150);
  }, [course, active]);

  // Fresh buyers land straight in their first lesson (mobile shows the lesson card
  // immediately) with a banner so the jump doesn't feel like a missed step.
  useEffect(() => {
    if (!justPurchased || !course || active === null) return;
    setMobileLesson(true);
    window.history.replaceState({}, "");
    // eslint-disable-next-line
  }, [justPurchased, course, active]);

  // Keep the lesson card aligned just under the sticky header (~1px of grey visible)
  // whenever the learner lands on a lesson on mobile — curriculum tap, Prev/Next,
  // Continue-to-next, or post-purchase drop-in. Measures the real header height
  // (breadcrumb row makes it taller for staff) and re-corrects after the lesson
  // swap animation settles.
  const alignLessonCard = () => {
    if (window.innerWidth >= 1024) return;
    const targetY = () => {
      const card = document.querySelector('[data-testid="lesson-card"]');
      if (!card) return null;
      const headerH = document.querySelector("header")?.getBoundingClientRect().height || 64;
      return { y: card.getBoundingClientRect().top + window.scrollY - headerH - 1, headerH, card };
    };
    setTimeout(() => {
      const t = targetY();
      if (t) window.scrollTo({ top: Math.max(0, t.y), behavior: "smooth" });
    }, 100);
    const correct = () => {
      const t = targetY();
      if (t && Math.abs(t.card.getBoundingClientRect().top - t.headerH - 1) > 4) {
        window.scrollTo({ top: Math.max(0, t.y) });
      }
    };
    setTimeout(correct, 600);
    setTimeout(correct, 900);
  };
  useEffect(() => {
    if (mobileLesson && active !== null) alignLessonCard();
    // eslint-disable-next-line
  }, [active, mobileLesson]);

  // Pre-warm the certificate previews the moment the course is complete, so the
  // certificates screen opens instantly instead of showing grey boxes while the
  // server renders them.
  const certWarmRef = useRef(false);
  useEffect(() => {
    if (certWarmRef.current || !course || (course.progress_percent || 0) < 100) return;
    certWarmRef.current = true;
    ["full", "wallet"].forEach((v) => {
      api.get(`/certificate/${course.id}/pdf`, { params: { format: "png", variant: v }, responseType: "blob" }).catch(() => {});
    });
  }, [course]);

  // Warning acknowledgment lasts only while this lesson stays open — switching
  // lessons (or reopening the course) shows it again. Choices are logged for analytics.
  const logHeavyChoice = (assetId, action) => {
    api.post("/heavy-warning-events", { lesson_asset_id: assetId, action }).catch(() => {});
  };
  const ackHeavy = (assetId) => { logHeavyChoice(assetId, "ready"); setHeavyAckedKey(assetId); };

  const load = async (preserveActive = false) => {
    try {
      let resumeData = resume;
      if (resumeData === undefined) {
        try { resumeData = (await api.get(`/progress/resume/${id}`)).data || null; }
        catch { resumeData = null; }
        setResume(resumeData);
      }
      const params = {};
      if (viewGrade) params.grade = viewGrade;
      if (lang && lang !== "en") params.lang = lang;
      const { data } = await api.get(`/courses/${id}`, { params });
      setCourse(data);
      // Arriving from the dashboard topic search: jump straight to the found lesson,
      // with the searched phrase highlighted in its transcript.
      const jumpId = loc.state?.jumpLessonId;
      if (jumpId && !preserveActive) {
        const ji = data.curriculum.findIndex((it) => it.lesson_asset_id === jumpId);
        if (ji >= 0) {
          if (loc.state?.jumpTerm) setJumpTerm(loc.state.jumpTerm);
          setActive(ji);
          if (window.innerWidth < 1024) setMobileLesson(true);
          window.history.replaceState({}, "");
          return data;
        }
      }
      let idx = data.curriculum.findIndex((it) => it.unlocked && !it.completed);
      // Resume where the learner left off (only if that lesson is still unlocked).
      if (resumeData?.lesson_asset_id) {
        const ri = data.curriculum.findIndex((it) => it.unlocked && it.lesson_asset_id === resumeData.lesson_asset_id);
        if (ri >= 0) idx = ri;
      }
      setActive((prev) => (preserveActive && prev !== null ? prev : (idx >= 0 ? idx : 0)));
      return data;
    } catch (e) {
      if (e?.response?.status === 403 && /seat has expired/i.test(e?.response?.data?.detail || "")) {
        setExpired(true);
      } else { setErr(formatApiError(e)); }
    }
  };
  useEffect(() => { load(); return () => clearTimeout(completeTimer.current); /* eslint-disable-next-line */ }, [id, lang]);

  // Remember the lesson the learner is on so a later visit picks up right here.
  useEffect(() => {
    const it = course?.curriculum?.[active];
    if (course && it?.unlocked) {
      api.post("/progress/resume", { course_id: course.id, lesson_asset_id: it.lesson_asset_id }).catch(() => {});
    }
    // eslint-disable-next-line
  }, [active]);

  if (expired) {
    return (
      <div className="bg-grain min-h-screen">
        <div className="max-w-xl mx-auto px-5 py-16">
          <div className="card text-center" data-testid="seat-expired-screen">
            <div className="w-16 h-16 mx-auto rounded-full grid place-items-center bg-[#FEF3C7] mb-4">
              <Lock size={34} weight="duotone" color="#B45309" />
            </div>
            <h1 className="text-2xl font-extrabold font-display mb-2">Your access has expired</h1>
            <p className="text-[#000000] mb-1">
              Your seat for the school year has ended. Ask your teacher to renew your enrollment so you can keep going.
            </p>
            <p className="text-sm text-[#000000] mb-6">
              Don't worry — your progress and any certificates you earned are safe and will be here when you return.
            </p>
            <Link to="/dashboard" className="btn-primary inline-flex items-center gap-1" data-testid="expired-back-to-dashboard">
              <ArrowLeft size={16} /> {t("Back to dashboard")}
            </Link>
          </div>
        </div>
      </div>
    );
  }
  if (err) return <div className="p-8 text-[#FF5100]">{err}</div>;
  if (!course) return <div className="p-8 text-[#000000]">Loading...</div>;

  const items = course.curriculum;
  const activeItem = items[active];
  const grace = course.seat_access && course.seat_access.state === "grace" ? course.seat_access : null;
  // Demo courses interleave locked lessons with playable ones — navigation skips the locked
  // ones so evaluators flow lesson-to-lesson without returning to the curriculum.
  const demoNav = !!course.demo_mode;
  const nextPlayable = (from) => { for (let i = from + 1; i < items.length; i++) { if (items[i].unlocked) return i; } return -1; };
  const prevPlayable = (from) => { for (let i = from - 1; i >= 0; i--) { if (items[i].unlocked) return i; } return -1; };

  // Registration gate: every learner (public AND school students) signs the waiver ONCE
  // before training begins. Staff/admin roles are exempt (they preview, they don't train),
  // and so are demo evaluators (board reps evaluate, they don't train).
  const STAFF_EXEMPT = ["super_admin", "school_board_admin", "school_admin", "teacher", "level2_instructor", "level1_instructor"];
  const isDemoEvaluator = String(user?.grade) === "DEMO";
  const needsWaiver = user && !user.waiver_signed && !STAFF_EXEMPT.includes(user.role) && !isDemoEvaluator;
  if (needsWaiver) {
    return (
      <div className="min-h-screen bg-grain flex items-start justify-center p-4 pt-8" data-testid="course-waiver-gate">
        <div className="w-full max-w-lg rounded-2xl bg-[#101318] p-6 shadow-2xl">
          <div className="text-center mb-4">
            <h1 className="text-2xl font-extrabold font-display till-text m-0">One quick thing before we begin</h1>
            <p className="text-white/60 text-sm mt-1">Sign the training waiver once — you'll never be asked again, for any training, workshop or event.</p>
          </div>
          <WaiverStep
            name={user.name || ""} email={user.email || ""} phone={user.phone || ""}
            source="online-course" refInfo={course.title}
            extra={{ principal_id: user.id, principal_kind: user.kind === "token" ? "token" : "user" }}
            submitLabel="Sign & start training"
            onSigned={async () => { await refreshMe(); }}
          />
        </div>
      </div>
    );
  }

  // Group the curriculum by pillar for the left menu: intro/foundational first, then each
  // pillar in its configured order. Each entry keeps its original flat index for navigation.
  const withIdx = items.map((it, idx) => ({ it, idx }));
  const searching = tQuery.trim().length >= 2;
  const groupsOrder = [
    { key: null, label: introLabel, color: "#52525B" },
    ...pillars.map((p) => ({ key: p.slug, label: p.name, color: p.color })),
  ];
  const knownKeys = new Set(groupsOrder.map((g) => g.key));
  const groupedCurriculum = [
    ...groupsOrder,
    // Any lesson whose pillar isn't in the current pillar list still gets shown.
    ...[...new Set(withIdx.map((x) => x.it.pillar).filter((p) => p && !knownKeys.has(p)))]
      .map((slug) => ({ key: slug, label: labelOf(slug), color: colorOf(slug) })),
  ]
    .map((g) => ({ ...g, entries: withIdx.filter((x) => (x.it.pillar || null) === g.key) }))
    .filter((g) => g.entries.length > 0);

  return (
    <ScreenGuard watermark={false}>
    <div className="bg-grain min-h-screen">
      <NdaGate />
      <div className="max-w-6xl mx-auto px-5 py-8">
        <Link to="/dashboard" className="text-[#000000] hover:text-white inline-flex items-center gap-1 mb-4" data-testid="back-to-dashboard-link">
          <ArrowLeft size={16} /> {t("Back to dashboard")}
        </Link>

        {/* "Your Journey" card hidden per user request (2026-06) — may be deleted later */}
        <div className={`hidden ${mobileLesson ? "" : ""}`}>
          <CourseJourneyMap items={items} active={active} colorOf={colorOf}
            view={journeyView} onViewChange={(v) => { setJourneyView(v); try { localStorage.setItem("courseNavView", v); } catch {} }}
            onSelect={(idx) => {
              setActive(idx);
              if (window.innerWidth < 1024) setMobileLesson(true);
            }} />
          <div className="mt-3 pt-3 border-t border-[#EDEEF0] flex flex-wrap gap-x-6 gap-y-2.5" data-testid="pillar-progress-rings">
            {groupedCurriculum.map((g) => {
              const counted = g.entries.filter((x) => !x.it.demo_locked);
              if (!counted.length) return null;
              const done = counted.filter((x) => x.it.completed).length;
              const pct = counted.length ? Math.round((done / counted.length) * 100) : 0;
              return (
                <div key={g.key || "intro"} className="flex items-center gap-2"
                  data-testid={`pillar-ring-${g.key || "intro"}`}
                  title={`${g.label}: ${done} of ${counted.length} lessons done (${pct}%)`}>
                  <PillarRing pct={pct} color={g.color} />
                  <div className="leading-tight">
                    <div className="text-xs font-bold text-[#000000]">{g.label}</div>
                    <div className="text-[11px] text-[#000000]">{done}/{counted.length}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid lg:grid-cols-[1fr_2fr] gap-6">
          <div className={`card min-w-0 lg:sticky lg:top-24 lg:self-start lg:max-h-[80vh] lg:overflow-y-auto ${mobileLesson ? "hidden lg:block" : ""}`}>
            <h2 className="text-lg font-bold font-display mb-3 flex items-center gap-2">
              <BookOpen size={20} weight="fill" color="#CCFF00" className="icon-outline" /> Curriculum
            </h2>
            {/* Curriculum search hidden per user request (2026-06) */}
            <div className="relative mb-3 hidden" data-testid="transcript-search">
              <MagnifyingGlass size={15} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A] pointer-events-none" />
              <input data-testid="transcript-search-input" value={tQuery} onChange={(e) => setTQuery(e.target.value)}
                placeholder="Search topics in lessons & transcripts…"
                className="input-base w-full pl-9 pr-8 text-sm" />
              {tQuery && (
                <button data-testid="transcript-search-clear" onClick={() => setTQuery("")} aria-label="Clear search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-transparent border-0 cursor-pointer text-[#71717A]">
                  <X size={14} weight="bold" />
                </button>
              )}
            </div>
            {searching && (
              <TranscriptSearchResults items={items} query={tQuery.trim()} onJump={(idx) => {
                setActive(idx);
                setJumpTerm(tQuery.trim());
                if (window.innerWidth < 1024) setMobileLesson(true);
              }} />
            )}
            <ol className={`flex flex-col gap-4 min-w-0 ${searching ? "hidden" : ""}`}>
              {groupedCurriculum.map((group) => (
                <li key={group.key || "__intro__"} data-testid={`curriculum-group-${group.key || "intro"}`}>
                  <div className="hidden lg:block"><div className="chip mb-2 text-xs font-bold" style={pillarChipStyle(group.color)}>{group.label}</div></div>
                  <div className="lg:hidden sticky top-[58px] z-20 -mx-5 px-5 py-2 bg-white border-b border-[#EDEEF0]" data-testid={`pillar-sticky-${group.key || "intro"}`}>
                    <div className="font-display font-extrabold text-base w-3/4 text-left truncate text-[#000000]">{group.label}</div>
                  </div>
                  <ol className="flex flex-col gap-2 lg:pl-3 lg:border-l-2" style={{ borderColor: group.color + "55" }}>
                    {group.entries.map(({ it, idx }) => {
                      const isActive = idx === active;
                      const vThumb = (it.videos && it.videos[0]?.thumbnail_url) || it.thumbnail_url;
                      const pct = lessonPct(it);
                      // Demo course: playable rows glow lime with a bold left edge; locked rows fade
                      // back — the 11 demo lessons pop out of the 64-row list at a glance.
                      const demoRow = course.demo_mode
                        ? (it.unlocked
                          ? { background: isActive ? "rgba(204,255,0,0.25)" : "rgba(204,255,0,0.14)",
                              border: `1px solid ${isActive ? "#A3B800" : "rgba(163,184,0,0.35)"}`,
                              borderLeft: "4px solid #A3B800" }
                          : { opacity: 0.55, filter: "grayscale(35%)" })
                        : {};
                      return (
                        <li key={idx}>
                          <button
                            data-testid={`curriculum-item-${idx}`}
                            onClick={() => {
                              setActive(idx);
                              if (window.innerWidth < 1024) setMobileLesson(true);
                            }}
                            className="w-full flex items-center gap-2 lg:gap-3 p-2 lg:p-3 rounded-xl text-left bg-transparent border-0"
                            style={{
                              background: isActive ? "rgba(204,255,0,0.08)" : "transparent",
                              border: `1px solid ${isActive ? "rgba(204,255,0,0.4)" : "transparent"}`,
                              cursor: "pointer",
                              ...demoRow,
                            }}
                          >
                            <div className="hidden lg:grid w-9 h-9 place-items-center rounded-full border-2 shrink-0" style={{
                              borderColor: it.completed ? "#CCFF00" : (it.skipped ? "#F59E0B" : (isActive ? "#CCFF00" : "#94979C")),
                              background: it.completed ? "linear-gradient(135deg,#00E5FF,#CCFF00)" : "transparent",
                              color: it.completed ? "#111827" : (it.skipped ? "#B45309" : "#52525B"),
                            }}>
                              {it.completed ? <CheckCircle size={18} weight="fill" />
                                : (it.skipped ? <Question size={16} weight="bold" />
                                : (!it.unlocked ? <Lock size={15} weight="bold" />
                                : (course.demo_mode
                                    ? <span className="text-[8px] font-extrabold text-black leading-none" data-testid={`curriculum-demo-badge-${idx}`}>DEMO</span>
                                    : <Play size={16} weight="fill" />)))}
                            </div>
                            <div className="lg:hidden w-5 shrink-0 grid place-items-center" data-testid={`curriculum-check-${idx}`}>
                              {it.completed ? <LimeCheck size={20} />
                                : it.skipped ? <Question size={15} weight="bold" color="#B45309" />
                                : !it.unlocked ? <Lock size={15} weight="bold" color="#52525B" />
                                : course.demo_mode ? <span className="text-[7px] font-extrabold text-black leading-none" data-testid={`curriculum-demo-badge-m-${idx}`}>DEMO</span>
                                : null}
                            </div>
                            {(it.thumbnail_url || vThumb) && (
                              <div className="relative shrink-0 w-[96px] lg:w-16">
                                <img src={vThumb || it.thumbnail_url} alt={it.title}
                                  className="w-full aspect-video object-cover rounded-lg lg:rounded-md border border-[#E4E4E7] lg:border-[#3a3d42]"
                                  data-testid={`curriculum-thumb-${idx}`} />
                                {!it.completed && pct > 0 ? (
                                  <ProgressRing pct={pct} />
                                ) : null}
                              </div>
                            )}
                            <div className="flex-1 min-w-0 flex gap-1.5">
                              <span className="text-sm font-bold lg:font-semibold text-[#000000] leading-snug shrink-0">{idx + 1}.</span>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-bold lg:font-semibold text-[#000000] leading-snug line-clamp-2" data-testid={`curriculum-title-${idx}`}>
                                  {it.title}
                                </div>
                                <div className="text-xs text-[#000000] flex items-center gap-1.5 mt-0.5">
                                  {it.duration_minutes} min
                                  {(it.documents || []).length > 0 && <span>· {(it.documents || []).length} doc{(it.documents || []).length>1?"s":""}</span>}
                                  {it.skipped && <span className="text-[#B45309] font-semibold">· skipped</span>}
                                </div>
                                {pct > 0 && !it.completed && (
                                  <div className="mt-1.5 h-1 rounded-full bg-[#EDEEF0] overflow-hidden" data-testid={`curriculum-progress-${idx}`}>
                                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: "#A3B800" }} />
                                  </div>
                                )}
                              </div>
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ol>
                </li>
              ))}
            </ol>
          </div>

          <div className={`card min-w-0 min-h-[400px] scroll-mt-[64px] ${mobileLesson ? "" : "hidden lg:block"}`} data-testid="lesson-card">
            <button className="lg:hidden btn-ghost flex items-center gap-1.5 text-[#000000] hover:text-[#000000] font-semibold text-sm mb-3" data-testid="back-to-curriculum"
              onClick={() => {
                setMobileLesson(false);
                setTimeout(() => {
                  document.querySelector(`[data-testid="curriculum-item-${active}"]`)?.scrollIntoView({ block: "center" });
                }, 60);
              }}>
              <ArrowLeft size={16} weight="bold" /> Back to curriculum
            </button>
            {justPurchased && (
              <div className="mb-3 rounded-xl border-2 px-3 py-2.5 text-sm font-semibold flex items-start gap-2" data-testid="just-purchased-banner"
                style={{ background: "rgba(204,255,0,0.15)", borderColor: "#A3B800", color: "#000000" }}>
                <CheckCircle size={18} weight="fill" color="#7a8a00" className="shrink-0 mt-0.5" />
                <span className="min-w-0">Payment confirmed — you're in! We've brought you straight to your first lesson so you can dive right in. A receipt is on its way to your email.</span>
                <button data-testid="just-purchased-dismiss" className="btn-ghost shrink-0 font-bold text-[#000000] hover:text-[#000000] px-1" aria-label="Dismiss"
                  onClick={() => setJustPurchased(false)}>✕</button>
              </div>
            )}
            {course.preview_mode && (
              <div className="mb-3 rounded-xl border-2 px-3 py-2 text-sm font-semibold" data-testid="preview-banner"
                style={{ background: "rgba(204,255,0,0.15)", borderColor: "#A3B800", color: "#000000" }}>
                Free preview — the first {course.free_preview_count || 5} lessons are on us. Buy the course to unlock everything.
              </div>
            )}
            {course.demo_mode && (
              <div className="mb-3 rounded-xl border-2 px-3 py-2 text-sm font-semibold" data-testid="demo-banner"
                style={{ background: "rgba(204,255,0,0.15)", borderColor: "#A3B800", color: "#000000" }}>
                Evaluator demo — a curated sample from every pillar is open below. Locked lessons show the full program's scope.
              </div>
            )}
            {course.renewal_grace && (
              <div className="mb-3 rounded-xl border-2 px-3 py-2 text-sm font-semibold" data-testid="grace-banner"
                style={{ background: "#FFFBEB", borderColor: "#FDE68A", color: "#92400E" }}>
                Your annual access expired on {course.renewal_grace.expired_on}. You have a {course.renewal_grace.grace_days}-day grace period — renew from the catalog to keep going.
              </div>
            )}
            <AnimatePresence mode="wait">
              {activeItem && (
                <motion.div key={activeItem.lesson_asset_id + "@" + active} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.25 }}>
                  {!activeItem.unlocked ? (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                      <Lock size={48} weight="duotone" color="#52525B" />
                      <h3 className="text-xl font-bold mt-3 font-display">{course.preview_mode ? "Free preview ended" : (activeItem.demo_locked ? "Included in the full course" : "Locked")}</h3>
                      <p className="text-[#000000] mt-1">{course.preview_mode ? "Buy the course to unlock every lesson, quiz and certificate." : (activeItem.demo_locked ? "This lesson is part of the complete program — the demo opens a curated sample from every pillar." : "Complete the previous lesson to unlock this.")}</p>
                      {course.preview_mode && (
                        <button className="btn-primary mt-4" onClick={() => nav("/catalog")} data-testid="preview-buy-button">
                          Buy the course
                        </button>
                      )}
                      {demoNav && nextPlayable(active) !== -1 && (
                        <button className="btn-primary mt-4" onClick={() => setActive(nextPlayable(active))} data-testid="demo-next-lesson-button">
                          Next demo lesson →
                        </button>
                      )}
                    </div>
                  ) : (
                    <LessonView
                      completedCount={items.filter((x) => x.completed).length}
                      showBuyNow={!!course.preview_mode && items.length > (course.free_preview_count || 5) && active === (course.free_preview_count || 5) - 1}
                      onBuyNow={() => nav("/catalog")}
                      item={activeItem}
                      freeNav={demoNav}
                      highlightTerm={jumpTerm}
                      courseId={course.id}
                      courseTitle={course.title}
                      heavyGate={isStudentViewer && activeItem.heavy_content && heavyAckedKey !== activeItem.lesson_asset_id}
                      onAckHeavy={() => ackHeavy(activeItem.lesson_asset_id)}
                      onHeavyBack={() => { logHeavyChoice(activeItem.lesson_asset_id, "back"); nav("/dashboard"); }}
                      helpEnabled={helpEnabled && isStudentViewer}
                      resume={resume || null}
                      onResumePatch={(patch) => setResume((r) => ({
                        ...(r || {}),
                        ...patch,
                        video_positions: { ...(r?.video_positions || {}), ...(patch.video_positions || {}) },
                      }))}
                      celebrationsEnabled={course.celebrations_enabled !== false}
                      lockForwardSeek={isStudentViewer}
                      onCompleted={async () => {
                        const prevPct = course?.progress_percent ?? 0;
                        const data = await load(true);
                        await refreshMe();
                        // The FINAL remaining lesson (any order) just got completed → big fanfare,
                        // slightly delayed so the coin celebration gets its moment first.
                        if (data && data.progress_percent >= 100 && prevPct < 100) {
                          completeTimer.current = setTimeout(() => setCourseComplete(true),
                            course?.celebrations_enabled !== false ? 3600 : 500);
                        }
                      }}
                      canAdvance={demoNav ? nextPlayable(active) !== -1 : active < items.length - 1}
                      onAdvance={() => setActive((i) => (demoNav ? (nextPlayable(i) !== -1 ? nextPlayable(i) : i) : Math.min(items.length - 1, i + 1)))}
                      hasPrev={demoNav ? prevPlayable(active) !== -1 : active > 0}
                      hasNext={demoNav ? nextPlayable(active) !== -1 : (active < items.length - 1 && !!items[active + 1]?.unlocked && !!(items[active]?.completed || items[active]?.skipped))}
                      onPrev={() => setActive((i) => (demoNav ? (prevPlayable(i) !== -1 ? prevPlayable(i) : i) : Math.max(0, i - 1)))}
                      onNext={() => setActive((i) => (demoNav ? (nextPlayable(i) !== -1 ? nextPlayable(i) : i) : Math.min(items.length - 1, i + 1)))}
                    />
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        <div className="mt-6">
        {grace && (
          <div data-testid="seat-grace-banner" className="mb-6 rounded-2xl border border-[#F59E0B] bg-[#FFFBEB] px-5 py-3 flex items-center gap-3">
            <Lock size={22} weight="duotone" color="#B45309" className="shrink-0" />
            <div className="text-sm text-[#92400E]">
              <span className="font-bold font-display">Your access expires soon</span> — your school is working on renewal.
              You can keep learning until <span className="font-bold">{grace.grace_until}</span>.
            </div>
          </div>
        )}

        <div className="card mb-6">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-3xl font-extrabold font-display mb-0">{course.title}</h1>
              {viewGrade && (course.demo_mode || isSchoolStaffViewer) && (
                <span data-testid="viewing-as-grade-badge" className="inline-block mt-2 text-xs font-display font-bold px-3 py-1 rounded-full" style={{background:"#0A0D14",color:"#CCFF00"}}>
                  {viewGrade === "DEMO" ? t("Viewing as Demo (evaluator)") : `${t("Viewing as Grade")} ${viewGrade.replace("G", "")}`}
                </span>
              )}
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-[0.2em] text-[#000000]">Progress</div>
              <div className="text-2xl font-extrabold font-display text-[#000000]" data-testid="course-progress-percent">
                {course.progress_percent}%
              </div>
              {(() => {
                const fmt = (m) => (m < 60 ? `${m}m` : `${Math.floor(m / 60)}h${m % 60 ? ` ${m % 60}m` : ""}`);
                const counted = (course.curriculum || []).filter((it) => !it.demo_locked);
                const total = counted.reduce((s, it) => s + (it.duration_minutes || 0), 0);
                const left = counted.reduce((s, it) => s + (!it.completed ? (it.duration_minutes || 0) : 0), 0);
                if (!total) return null;
                return (
                  <div className="text-xs text-[#000000] mt-1 whitespace-nowrap" data-testid="course-time-estimate">
                    ~{fmt(total)} {t("total")} · <b>~{fmt(left)} {t("left")}</b>
                  </div>
                );
              })()}
            </div>
          </div>
          <p className="text-[#000000] text-sm font-semibold whitespace-nowrap overflow-hidden text-ellipsis mt-1 mb-0" data-testid="course-description-line">{course.description}</p>
          <div className="mt-3 progress-track">
            <div className="progress-fill" style={{ width: `${course.progress_percent}%` }} />
          </div>
          {course.progress_percent === 100 && (
            <button data-testid="view-certificate-button" onClick={() => nav(`/certificate/${course.id}${viewGrade ? `?grade=${viewGrade}` : ""}`)} className="btn-primary mt-4">
              View Certificate
            </button>
          )}
        </div>

        <CoinStreakBanner celebrationsEnabled={course.celebrations_enabled !== false} className="mb-6" />
        </div>
      </div>
      {courseComplete && (
        <CourseCompleteCelebration
          course={course}
          grade={viewGrade}
          soundOn={course.celebrations_enabled !== false && localStorage.getItem("coinSoundsOff") !== "1"}
          onClose={() => setCourseComplete(false)}
        />
      )}
    </div>
    </ScreenGuard>
  );
}

// Streak shield saves + milestone bonuses arrive on the completion response —
// surface them as toasts so they land no matter which coin set is playing.
function notifyStreakExtras(data) {
  if (data.freeze_used) {
    toast.info(`🛡️ A streak shield saved your ${data.streak}-day streak!`, { duration: 6500 });
  }
  (data.streak_bonus?.milestones || []).forEach((m, i) => {
    setTimeout(() => toast.success(`🔥 ${m.days}-day streak milestone — +${m.coins.toLocaleString()} bonus coins!`, { duration: 7000 }), 1500 + i * 900);
  });
}

function LessonView({ item, courseId, courseTitle, heavyGate = false, onAckHeavy, onHeavyBack, helpEnabled = false, onCompleted, canAdvance, onAdvance, celebrationsEnabled = true, lockForwardSeek = false, hasPrev = false, hasNext = false, onPrev, onNext, resume = null, onResumePatch, showBuyNow = false, onBuyNow, completedCount = 0, highlightTerm = "", freeNav = false }) {
  const heavyNav = useNavigate();
  const { labelOf, colorOf } = usePillars();
  const { t, lang, setLang } = useI18n();
  const videos = (item.videos && item.videos.length)
    ? item.videos
    : (item.video_url ? [{ id: "__legacy__", url: item.video_url, aspect_ratio: "16:9", label: "" }] : []);
  const [watchedIds, setWatchedIds] = useState(new Set());
  const [videoWatched, setVideoWatched] = useState(false);
  const [openedDocs, setOpenedDocs] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [quizState, setQuizState] = useState(null); // {questions, answers, result}
  // Forced rewatch after quiz fail #2: bump the epoch to fully remount the video
  // (resets its internal furthest-watched marker) and lock forward seeking.
  const [rewatchEpoch, setRewatchEpoch] = useState(0);
  const [rewatchActive, setRewatchActive] = useState(false);
  const myResume = resume && resume.lesson_asset_id === item.lesson_asset_id ? resume : null;

  // Fire-and-forget persistence of "where I am" so a returning learner picks up here.
  // Also patch the parent's local copy so in-app navigation (leave lesson → come back)
  // resumes correctly without a full page reload.
  const saveResume = (patch) => {
    onResumePatch && onResumePatch({ lesson_asset_id: item.lesson_asset_id, ...patch });
    return api.post("/progress/resume", { course_id: courseId, lesson_asset_id: item.lesson_asset_id, ...patch }).catch(() => {});
  };

  useEffect(() => {
    // Already-completed lessons unlock immediately on revisit — no need to re-watch
    // the video or re-open resources just to retake the quiz.
    if (item.completed) {
      setVideoWatched(true);
      setOpenedDocs(new Set((item.documents || []).map(d => d.id)));
      return;
    }
    if (item.content_type === "video" && videos.length === 0) setVideoWatched(true);
    // Restore gate state + any in-flight quiz from the saved resume point.
    if (myResume?.gate?.video_watched) setVideoWatched(true);
    if (myResume?.gate?.watched_video_ids?.length) setWatchedIds(new Set(myResume.gate.watched_video_ids));
    if (myResume?.gate?.opened_docs?.length) setOpenedDocs(new Set(myResume.gate.opened_docs));
    if (myResume?.quiz?.questions?.length) {
      setQuizState({
        questions: myResume.quiz.questions, answers: myResume.quiz.answers || {}, result: null,
        threshold: myResume.quiz.threshold, no_fail: myResume.quiz.no_fail,
        allQuestions: myResume.quiz.allQuestions, resumeIdx: myResume.quiz.idx || 0,
      });
    }
    setGateHydrated(true);
    // eslint-disable-next-line
  }, []);

  const docs = item.documents || [];
  const hasQuiz = !!item.quiz;
  const docsOpened = docs.every(d => openedDocs.has(d.id));
  const ready = videoWatched && docsOpened;
  // No-quiz lesson coin celebration: plays 0.25s after the video fully finishes.
  const [completionCeleb, setCompletionCeleb] = useState(null);

  // Pause all lesson videos at each takeover phase: quiz opens, result/coin set
  // shows, or the completion celebration appears. Nothing ever auto-resumes — the
  // video stays paused afterwards and the user presses play if they want more.
  const pausePhase = quizState ? (quizState.result ? "result" : "quiz") : (completionCeleb ? "celeb" : "");
  useEffect(() => {
    if (!pausePhase) return;
    document.querySelectorAll('video[data-testid^="lesson-video-"]').forEach((v) => {
      try { if (!v.paused) v.pause(); } catch { /* no-op */ }
    });
  }, [pausePhase]);
  const [celebDone, setCelebDone] = useState(false);
  const [levelUp, setLevelUp] = useState(null);
  const [diamondCeleb, setDiamondCeleb] = useState(null);
  // Extra rewards (milestone toasts, shield saves, level-up fireworks) are queued
  // and only released once the coin set has fully finished playing.
  const [pendingExtras, setPendingExtras] = useState(null);
  const [extrasReady, setExtrasReady] = useState(false);

  useEffect(() => {
    if (!pendingExtras || !extrasReady) return;
    const data = pendingExtras;
    setPendingExtras(null);
    setExtrasReady(false);
    notifyStreakExtras(data);
    checkLevelUp(data);
    if ((data.diamonds_forged || 0) > 0) {
      setTimeout(() => setDiamondCeleb({ forged: data.diamonds_forged, total: data.diamonds || data.diamonds_forged }), 1100);
    } else if (data.gems_forged && ((data.gems_forged.rubies_gained || 0) > 0 || (data.gems_forged.emeralds_gained || 0) > 0)) {
      const g = data.gems_forged;
      setTimeout(() => {
        if (g.rubies_gained > 0) toast.success(`❤️ RUBY forged! 5 emeralds fused into a ruby${g.rubies_gained > 1 ? ` ×${g.rubies_gained}` : ""}`, { duration: 5000 });
        else toast.success(`💚 Emerald forged! 20 coins became an emerald${g.emeralds_gained > 1 ? ` ×${g.emeralds_gained}` : ""}`, { duration: 5000 });
      }, 1100);
    }
    // eslint-disable-next-line
  }, [pendingExtras, extrasReady]);

  // Crossing into Silver / Gold / Self-Agent triggers the full-screen level-up moment.
  const checkLevelUp = (data) => {
    const gained = (data.xp_awarded || 0) + (data.streak_bonus?.coins || 0);
    if (!gained || data.xp == null) return;
    const before = levelOf(data.xp - gained);
    const after = levelOf(data.xp);
    if (after.idx > before.idx) setTimeout(() => setLevelUp(after), 600);
  };
  const [videoEnded, setVideoEnded] = useState(false);
  const isVideoLesson = item.content_type === "video" && videos.length > 0;
  // Coin sounds can be muted from the header hamburger menu (per device).
  const soundOn = celebrationsEnabled && localStorage.getItem("coinSoundsOff") !== "1";
  // Keep Next locked from the moment the video ends until the coins finish doing their thing.
  const celebPending = !hasQuiz && isVideoLesson && videoEnded && !celebDone && (!item.completed || !!completionCeleb);

  useEffect(() => {
    if (!hasQuiz && isVideoLesson && videoEnded && ready && !item.completed && !completionCeleb && !submitting) {
      const t = setTimeout(markComplete, 250);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line
  }, [videoEnded, ready]);

  const markVideoWatched = (vid) => setWatchedIds(prev => {
    if (prev.has(vid)) return prev; // idempotent — no new Set, no needless re-render
    const n = new Set(prev); n.add(vid);
    if (n.size >= videos.length) { setVideoWatched(true); setRewatchActive(false); }
    return n;
  });
  const onDocClick = (docId) => setOpenedDocs(s => { const n = new Set(s); n.add(docId); return n; });

  // Persist gate state (video watched / per-video progress / docs opened) so a
  // returning learner keeps credit for videos already finished in a past session.
  // Saves are held until the restore above has hydrated — an eager save with the
  // initial empty state raced the restore and could wipe saved progress.
  const [gateHydrated, setGateHydrated] = useState(false);
  useEffect(() => {
    if (!gateHydrated || item.completed) return;
    saveResume({ gate: { video_watched: videoWatched, opened_docs: [...openedDocs], watched_video_ids: [...watchedIds] } });
    // eslint-disable-next-line
  }, [gateHydrated, videoWatched, openedDocs, watchedIds]);

  const markComplete = async () => {
    setSubmitting(true); setErr("");
    try {
      const { data } = await api.post("/progress/lesson-complete", {
        course_id: courseId, lesson_asset_id: item.lesson_asset_id,
      });
      if (!data.already_completed) { setCompletionCeleb(data); setCelebDone(false); setExtrasReady(false); setPendingExtras(data); }
      await onCompleted();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  const startQuiz = async () => {
    setErr("");
    try {
      const { data } = await api.get(`/lessons/${item.lesson_asset_id}/quiz?course_id=${courseId}${lang && lang !== "en" ? `&lang=${lang}` : ""}`);
      setQuizState({ questions: data.questions, answers: {}, result: null, threshold: data.pass_threshold, no_fail: data.no_fail, allQuestions: data.all_questions_mode });
      saveResume({ quiz: { questions: data.questions, answers: {}, threshold: data.pass_threshold, no_fail: data.no_fail, allQuestions: data.all_questions_mode, idx: 0 } });
    } catch (e) { setErr(formatApiError(e)); }
  };

  const setAnswer = (qid, oi) => setQuizState(s => {
    const answers = { ...s.answers, [qid]: oi };
    saveResume({ quiz: { questions: s.questions, answers, threshold: s.threshold, no_fail: s.no_fail, allQuestions: s.allQuestions, idx: 0 } });
    return { ...s, answers };
  });

  const submitQuiz = async (answersOverride) => {
    const answers = answersOverride || quizState.answers;
    if (Object.keys(answers).length !== quizState.questions.length) {
      setErr("Please answer every question."); return;
    }
    setSubmitting(true); setErr("");
    try {
      const { data } = await api.post("/progress/lesson-quiz-attempt", {
        course_id: courseId, lesson_asset_id: item.lesson_asset_id, answers,
        lang: lang && lang !== "en" ? lang : null,
      });
      setQuizState(s => ({ ...s, result: data }));
      saveResume({ clear_quiz: true });
      if (data.passed) {
        setExtrasReady(false);
        setPendingExtras(data);
        await onCompleted();
      } else if (data.rewatch_required) {
        // School fail #2: force a fresh rewatch of the video before the next attempt.
        setVideoWatched(false); setWatchedIds(new Set());
        setRewatchEpoch(e => e + 1); setRewatchActive(true);
      }
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  // School student: reveal answers, mark the lesson incomplete-but-skipped, and move on.
  const comeBackLater = async () => {
    setSubmitting(true); setErr("");
    try {
      const { data } = await api.post("/progress/quiz-come-back-later", {
        course_id: courseId, lesson_asset_id: item.lesson_asset_id,
        lang: lang && lang !== "en" ? lang : null,
      });
      setQuizState(s => ({ ...s, reveal: data.answers }));
      saveResume({ clear_quiz: true });
      await onCompleted();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <div data-testid="lesson-view">
      {levelUp && <LevelUpCelebration level={levelUp} soundOn={celebrationsEnabled} onClose={() => setLevelUp(null)} />}
      {diamondCeleb && <DiamondCelebration forged={diamondCeleb.forged} total={diamondCeleb.total} soundOn={celebrationsEnabled} onClose={() => setDiamondCeleb(null)} />}
      {heavyGate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="heavy-content-overlay">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-md" />
          <div className="relative w-full max-w-lg rounded-2xl border border-[#C4C8CE] bg-white p-6 shadow-2xl text-center">
            <Warning size={44} weight="duotone" color="#DC2626" className="mx-auto" />
            <h3 className="text-xl font-extrabold font-display mt-2">{t("Heavy Content Warning")}</h3>
            {item.heavy_content_description && (
              <p className="text-sm font-semibold text-[#7F1D1D] mt-2" data-testid="heavy-content-description">{item.heavy_content_description}</p>
            )}
            <p className="text-sm text-[#000000] mt-3">
              {t("Take a breath — it's okay to feel things while learning this. The Help icon")} (<Heart size={13} weight="fill" color="#DC2626" className="inline" />) {t("in the top corner is always there if you need someone to talk to.")}
            </p>
            <div className="flex flex-col sm:flex-row gap-2 justify-center mt-5">
              <button data-testid="heavy-content-ready-button" className="btn-primary" onClick={onAckHeavy}>
                {t("I'm ready — start the lesson")}
              </button>
              <button data-testid="heavy-content-back-button" className="btn-secondary" onClick={onHeavyBack}>
                {t("Take a moment — go back")}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="chip" style={pillarChipStyle(colorOf(item.pillar))}>{labelOf(item.pillar)}</span>
        {item.completed && (
          <span className="chip" style={{ background: "rgba(204,255,0,0.2)", color: "#111827", border: "1px solid rgba(120,150,0,0.5)" }}>
            <CheckCircle size={12} weight="fill" color="#CCFF00" className="icon-outline" /> Completed · revisit any time
          </span>
        )}
      </div>
      <RewardsBar />
      <h2 className="text-2xl font-bold font-display mt-3 mb-3">{item.title}</h2>

      {item.content_type === "video" && videos.length > 0 && (
        <div className="flex flex-col gap-3" data-testid="lesson-videos">
          {videos.map((v, vi) => (
            <VideoBlock
              key={`${v.id || vi}-rw${rewatchEpoch}`} video={v} index={vi} total={videos.length}
              celebrationsEnabled={celebrationsEnabled}
              lockForwardSeek={rewatchActive || (lockForwardSeek && !item.completed && !watchedIds.has(v.id || `v${vi}`))}
              initialTime={rewatchEpoch === 0 ? (resume?.video_positions?.[`${item.lesson_asset_id}:${vi}`] || 0) : 0}
              onProgress={(t) => saveResume({ video_positions: { [`${item.lesson_asset_id}:${vi}`]: t } })}
              onWatched={() => markVideoWatched(v.id || `v${vi}`)}
              watched={watchedIds.has(v.id || `v${vi}`)}
              onEnded={() => setVideoEnded(true)}
              lessonId={item.lesson_asset_id}
              defaultLang={lang === "fr" ? "fr" : "en"}
            />
          ))}
          {item.transcript && (
            <TranscriptBlock text={item.transcript} highlight={highlightTerm} lessonId={item.lesson_asset_id}
              defaultLang={lang === "fr" ? "fr" : "en"} onSticky={(l) => setLang(l)} />
          )}
          {videoWatched && (
            <div className="self-start px-2 py-1 rounded-full bg-[#CCFF00]/20 text-[#7a8a00] text-xs font-display font-bold flex items-center gap-1" data-testid="video-watched-badge">
              <CheckCircle size={12} weight="fill" /> {videos.length > 1 ? "All videos watched" : "Video watched"}
            </div>
          )}
        </div>
      )}
      {item.content_type === "video" && videos.length > 0 && (
        <div className="mt-3 flex flex-col sm:flex-row sm:flex-wrap gap-2" data-testid="lesson-action-buttons">
          <SosButton variant="lesson" />
          {helpEnabled && (
            <button data-testid="lesson-help-button"
              className="btn-ghost text-xs font-semibold flex items-center gap-1.5 text-[#000000] border border-[#C4C8CE] rounded-full px-3 py-1.5 bg-white hover:border-[#CCFF00] cursor-pointer"
              onClick={() => heavyNav("/inbox", { state: { compose: {
                body: `I need help with the lesson \u201c${item.title}\u201d. My question is: `,
                lesson_context: { course_id: courseId, course_title: courseTitle, lesson_asset_id: item.lesson_asset_id, lesson_title: item.title },
              } } })}>
              <ChatCircleText size={15} weight="duotone" /> Help with this lesson
            </button>
          )}
          <FeedbackButton variant="lesson" context={`Lesson: ${item.title} · ${courseTitle || ""}`} />
        </div>
      )}
      {item.content_type === "text" && (
        <>
        <div className="mb-2 flex justify-end"><ReadAloudButton text={item.text_body} /></div>
        <div className="prose prose-invert max-w-none whitespace-pre-wrap text-[#000000] leading-relaxed bg-[#DCDEE2] p-5 rounded-xl border border-[#C4C8CE]" data-testid="lesson-text" onScroll={() => setVideoWatched(true)} onClick={() => setVideoWatched(true)}>
          {item.text_body}
        </div>
        </>
      )}

      {/* Documents */}
      {docs.length > 0 && (
        <div className="mt-4">
          <div className="text-xs uppercase tracking-[0.2em] text-[#000000] mb-2 flex items-center gap-1.5">
            <FileText size={14} /> Resources ({docs.length})
          </div>
          <div className="flex flex-col gap-2">
            {docs.map(d => {
              const opened = openedDocs.has(d.id);
              const unlocked = !isVideoLesson || videoWatched || item.completed;
              if (!unlocked) return (
                <div key={d.id} title="Watch the video to unlock this download"
                  className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 px-3 py-2 rounded-xl bg-[#DCDEE2] border border-[#C4C8CE] cursor-not-allowed"
                  data-testid={`lesson-doc-${d.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText size={18} weight="duotone" color="#1F2937" className="shrink-0" />
                    <span className="text-sm text-[#000000] break-words min-w-0">{d.name}</span>
                  </div>
                  <span className="flex items-center gap-1 text-xs font-semibold text-[#000000]">
                    <DownloadSimple size={14} color="#1F2937" className="shrink-0" /> Watch the video to unlock
                  </span>
                </div>
              );
              return (
                <a key={d.id} href={d.url} target="_blank" rel="noopener noreferrer"
                   onClick={() => onDocClick(d.id)}
                   className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl bg-white border-2 border-[#CCFF00] hover:bg-[rgba(204,255,0,0.12)] transition-colors"
                   data-testid={`lesson-doc-${d.id}`}
                >
                  <div className="flex items-center gap-2">
                    <FileText size={18} weight="duotone" color="#00E5FF" />
                    <span className="text-sm font-semibold text-[#000000]">{d.name}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs shrink-0">
                    {opened && <span className="text-[#5a7a00] flex items-center gap-0.5"><Eye size={12} /> opened</span>}
                    <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#CCFF00] text-[#000000] font-display font-bold" data-testid={`lesson-doc-download-${d.id}`}>
                      <DownloadSimple size={14} weight="bold" /> Download
                    </span>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      )}

      {err && <div className="text-[#FF5100] mt-3">{err}</div>}

      {/* Quiz section or Mark Complete */}
      <div className="mt-6 p-5 rounded-2xl border border-[#C4C8CE] bg-[#DCDEE2]" data-testid="lesson-action-panel">
        {hasQuiz ? (
          quizState ? (
            quizState.result ? (
              <QuizResult result={quizState.result} threshold={quizState.threshold} celebrationsEnabled={soundOn} completedCount={completedCount}
                onRetry={startQuiz} canAdvance={canAdvance}
                onCelebDone={() => setExtrasReady(true)}
                onContinue={() => { setQuizState(null); onAdvance && onAdvance(); }}
                hasPrev={hasPrev} onPrev={onPrev}
                videoWatched={videoWatched} reveal={quizState.reveal}
                onComeBackLater={comeBackLater} submitting={submitting} />
            ) : (
              <>
                <div className="text-sm text-[#000000]" data-testid="quiz-in-progress-note">{t("Quiz in progress — your coins stay right here.")}</div>
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="quiz-overlay">
                  <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
                  <div className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl border border-[#C4C8CE] bg-[#DCDEE2] p-5 shadow-2xl">
                    {quizState.no_fail ? (
                      <NoFailQuiz questions={quizState.questions} submitting={submitting} allQuestions={quizState.allQuestions}
                        initialIdx={quizState.resumeIdx || 0} initialAnswers={quizState.answers || {}}
                        onAutosave={(nidx, answers) => saveResume({ quiz: { questions: quizState.questions, answers, threshold: quizState.threshold, no_fail: true, allQuestions: quizState.allQuestions, idx: nidx } })}
                        onFinish={(answers) => submitQuiz(answers)} />
                    ) : (
                      <ActiveQuiz quizState={quizState} setAnswer={setAnswer} onSubmit={submitQuiz} submitting={submitting} />
                    )}
                  </div>
                </div>
              </>
            )
          ) : (
            <QuizGate ready={ready} videoWatched={videoWatched} docsOpened={docsOpened} totalDocs={docs.length} quizMeta={item.quiz} onStart={startQuiz} completed={item.completed} />
          )
        ) : completionCeleb ? (
          <LessonCelebration
            completedCount={completedCount}
            coinsAwarded={completionCeleb.coins_awarded ?? completionCeleb.xp_awarded ?? 0}
            totalCoins={completionCeleb.coins_balance ?? completionCeleb.xp ?? 0}
            soundOn={soundOn}
            onDone={() => { setCelebDone(true); setExtrasReady(true); }}
          />
        ) : (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm text-[#000000]">
              {item.completed
                ? "Lesson complete — revisit any time."
                : isVideoLesson
                  ? (docs.length ? "No quiz this lesson — finish the video and open every resource." : "No quiz this lesson.")
                  : ready ? "All set — mark this lesson complete to continue." : "Read the lesson" + (docs.length ? " and open every resource" : "") + " to enable completion."}
            </div>
            {item.completed ? (
              <div className="text-[#000000] font-display font-semibold flex items-center gap-1" data-testid="lesson-completed-indicator">
                <CheckCircle size={18} weight="fill" /> Completed
              </div>
            ) : !isVideoLesson ? (
              <button data-testid="lesson-complete-button" className="btn-primary flex items-center gap-2"
                onClick={markComplete} disabled={!ready || submitting}>
                {submitting ? t("Saving...") : t("Mark complete")}
                <CheckCircle size={16} weight="bold" />
              </button>
            ) : null}
          </div>
        )}
      </div>

      {/* Prev/Next lesson nav — hidden during the pass celebration (its own row
          carries a Prev, and Continue replaces Next there). */}
      {!(quizState && quizState.result && quizState.result.passed) && (
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#C4C8CE]">
          <button data-testid="prev-item-button" className="btn-secondary" disabled={!hasPrev} onClick={() => onPrev && onPrev()}>
            <ArrowLeft size={16} className="inline mr-1" /> {t("Prev")}
          </button>
          {(() => {
            const nextReady = hasNext && (freeNav || !(videos.length > 0 && !videoWatched)) && !(!!completionCeleb && !celebDone) && !celebPending;
            return (
              <button data-testid="next-item-button"
                className={nextReady ? "btn-primary" : "btn-secondary"}
                disabled={!nextReady} onClick={() => onNext && onNext()}
                title={videos.length > 0 && !videoWatched ? "Finish the video to unlock the next lesson" : undefined}>
                {t("Next")} <ArrowRight size={16} className="inline ml-1" />
              </button>
            );
          })()}
        </div>
      )}
      {/* Last free-preview lesson: floating Buy Now so the path forward is obvious */}
      {showBuyNow && (
        <div className="sticky bottom-4 z-30 flex justify-center pointer-events-none mt-5">
          <button data-testid="preview-buy-now-floating" onClick={() => onBuyNow && onBuyNow()}
            className="btn-primary pointer-events-auto shadow-2xl px-6 py-3 text-base flex items-center gap-2"
            style={{ boxShadow: "0 10px 30px rgba(163,184,0,0.5)" }}>
            <ShoppingCartSimple size={20} weight="bold" /> Buy now — unlock the full course
          </button>
        </div>
      )}
    </div>
  );
}

function VideoBlock({ video, index, total, onWatched, onEnded, celebrationsEnabled = true, lockForwardSeek = false, initialTime = 0, onProgress, watched = false, lessonId = null, defaultLang = "en" }) {
  const declaredRatios = { "16:9": 16 / 9, "9:16": 9 / 16, "1:1": 1, "4:3": 4 / 3, "21:9": 21 / 9 };
  const ar = video.aspect_ratio || "16:9";
  const [realRatio, setRealRatio] = useState(null);
  const ratio = realRatio || declaredRatios[ar] || 16 / 9;
  const maxVh = ratio < 1.05 ? 48 : 38;
  const url = video.url || "";
  const isEmbed = /youtube|youtu\.be|vimeo/i.test(url);
  const isBunny = url.startsWith("bunny://");
  const bunnyGuid = isBunny ? url.slice(8) : null;

  const maxTimeRef = useRef(0);
  const celebratedRef = useRef(false);
  const endGuardRef = useRef(null);
  useEffect(() => () => { if (endGuardRef.current) clearTimeout(endGuardRef.current); }, []);
  const resetEndGuards = () => {
    celebratedRef.current = false;
    if (endGuardRef.current) { clearTimeout(endGuardRef.current); endGuardRef.current = null; }
  };
  const videoRef = useRef(null);

  // ---- Subtitles (CC): English from Whisper timings, French translated + cached server-side ----
  const [cc, setCc] = useState("off"); // off | en | fr
  const [ccUrls, setCcUrls] = useState({});
  const [ccBusy, setCcBusy] = useState(null); // lang being fetched
  const [ccErr, setCcErr] = useState("");
  const enableCc = async (lang) => {
    setCcErr("");
    if (lang === "off") { setCc("off"); return; }
    if (!ccUrls[lang]) {
      setCcBusy(lang);
      try {
        const { data } = await api.get(`/learning/lesson-assets/${lessonId}/captions/${video.id}/${lang}`);
        const url = URL.createObjectURL(new Blob([data.vtt], { type: "text/vtt" }));
        setCcUrls((m) => ({ ...m, [lang]: url }));
      } catch {
        setCcErr(lang === "fr" ? "Sous-titres indisponibles pour cette vidéo." : "Captions aren't available for this video yet.");
        setCcBusy(null);
        return;
      }
      setCcBusy(null);
    }
    setCc(lang);
  };
  useEffect(() => {
    const apply = () => {
      const v = videoRef.current;
      if (!v) return;
      for (const t of v.textTracks) t.mode = t.language === cc ? "showing" : "disabled";
    };
    apply();
    const t = setTimeout(apply, 300); // tracks attach async
    return () => clearTimeout(t);
  }, [cc, ccUrls]);
  useEffect(() => {
    if (defaultLang === "fr" && lessonId && video?.id) enableCc("fr");
    // eslint-disable-next-line
  }, [defaultLang, lessonId, video?.id]);

  // ---- French voice-over (TTS stopgap): synced <audio> over the muted video ----
  const [voice, setVoice] = useState("off"); // off | fr
  const [voiceState, setVoiceState] = useState(""); // "" | loading | ready | error
  const [voiceUrl, setVoiceUrl] = useState("");
  const audioRef = useRef(null);
  const voiceTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(voiceTimerRef.current), []);
  const voiceOnRef = useRef(false);
  const fetchVoice = async () => {
    try {
      const { data } = await api.get(`/learning/lesson-assets/${lessonId}/voice/${video.id}/fr`);
      if (!voiceOnRef.current) return; // user switched back to Original mid-flight
      if (data.status === "ready") { setVoiceUrl(data.url); setVoiceState("ready"); }
      else if (data.status === "generating") {
        setVoiceState("loading");
        voiceTimerRef.current = setTimeout(fetchVoice, 7000);
      } else setVoiceState("error");
    } catch { if (voiceOnRef.current) setVoiceState("error"); }
  };
  const enableVoice = (on) => {
    clearTimeout(voiceTimerRef.current);
    voiceOnRef.current = on;
    if (!on) { setVoice("off"); return; }
    setVoice("fr");
    if (voiceState !== "ready") fetchVoice();
  };
  useEffect(() => {
    if (defaultLang === "fr" && lessonId && video?.id && !isEmbed) enableVoice(true);
    // eslint-disable-next-line
  }, [defaultLang, lessonId, video?.id]);
  const voiceActive = voice === "fr" && voiceState === "ready" && !!voiceUrl;
  useEffect(() => {
    const v = videoRef.current;
    const a = audioRef.current;
    if (!v) return undefined;
    if (!voiceActive || !a) { if (a) a.pause(); return undefined; }
    const wasMuted = v.muted;
    v.muted = true;
    const sync = () => { try { a.currentTime = v.currentTime; a.playbackRate = v.playbackRate; } catch { /* noop */ } };
    const onPlay = () => { sync(); a.play().catch(() => {}); };
    const onPause = () => a.pause();
    const onRate = () => { a.playbackRate = v.playbackRate; };
    const onTime = () => {
      if (!v.muted) v.muted = true; // re-mute if native controls unmuted the original audio
      if (!v.paused && Math.abs(a.currentTime - v.currentTime) > 0.45) sync();
    };
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    v.addEventListener("seeked", sync);
    v.addEventListener("ratechange", onRate);
    v.addEventListener("timeupdate", onTime);
    if (!v.paused) onPlay();
    return () => {
      v.muted = wasMuted;
      a.pause();
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("seeked", sync);
      v.removeEventListener("ratechange", onRate);
      v.removeEventListener("timeupdate", onTime);
    };
  }, [voiceActive, voiceUrl]);

  const [playing, setPlaying] = useState(false);
  const [decodeFailed, setDecodeFailed] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const lastSaveRef = useRef(0);
  const appliedInitialRef = useRef(false);

  // Mid-playback network blips (common on phones) fire the <video> error event even though
  // the file itself is fine. Auto-recover: reload the element and resume where it stopped,
  // up to 3 times per incident, before showing the fatal overlay (which now has a retry button).
  const retryRef = useRef({ count: 0, lastAt: 0, timer: null });
  const resumePosRef = useRef(0);
  useEffect(() => () => clearTimeout(retryRef.current.timer), []);

  // ---- Bunny Stream (adaptive HLS) ----
  const hlsRef = useRef(null);
  const [bunnyState, setBunnyState] = useState(isBunny ? "loading" : "ready");
  const attachBunny = async (resumeAt = 0) => {
    const v = videoRef.current;
    if (!v || !bunnyGuid) return;
    try {
      const { data } = await api.get(`/learning/bunny-playback/${bunnyGuid}`);
      if (data.status !== 4) { setBunnyState("processing"); return; }
      if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
      const seekBack = () => {
        if (resumeAt > 0.5) { try { v.currentTime = resumeAt; } catch { /* noop */ } }
        if (resumeAt > 0.5) v.play().catch(() => {});
      };
      if (v.canPlayType("application/vnd.apple.mpegurl")) {
        v.src = data.hls_url; // Safari / iOS native HLS
        v.addEventListener("loadedmetadata", seekBack, { once: true });
      } else {
        const Hls = (await import("hls.js")).default;
        if (Hls.isSupported()) {
          const hls = new Hls({ enableWorker: true });
          hlsRef.current = hls;
          hls.on(Hls.Events.ERROR, (_e, d) => {
            if (!d.fatal) return;
            if (d.type === "networkError") hls.startLoad();
            else if (d.type === "mediaError") hls.recoverMediaError();
            else scheduleRecovery();
          });
          hls.loadSource(data.hls_url);
          hls.attachMedia(v);
          v.addEventListener("loadedmetadata", seekBack, { once: true });
        } else {
          v.src = data.mp4_url; // ancient browsers: 720p MP4 fallback
          v.addEventListener("loadedmetadata", seekBack, { once: true });
        }
      }
      setBunnyState("ready");
    } catch {
      setBunnyState((s) => (s === "loading" ? "processing" : s));
    }
  };
  useEffect(() => {
    if (!isBunny) return;
    attachBunny(0);
    return () => { if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bunnyGuid]);
  useEffect(() => {
    if (bunnyState !== "processing") return;
    const t = setInterval(() => attachBunny(0), 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bunnyState]);

  const reloadAndResume = () => {
    const v = videoRef.current;
    if (!v) return;
    const at = resumePosRef.current;
    if (isBunny) { attachBunny(at); return; } // re-mints the signed URL too (handles expiry)
    try { v.load(); } catch { setDecodeFailed(true); return; }
    const onMeta = () => {
      v.removeEventListener("loadedmetadata", onMeta);
      if (at > 0.5 && isFinite(v.duration) && at < v.duration - 1) { try { v.currentTime = at; } catch { /* noop */ } }
      v.play().catch(() => {});
    };
    v.addEventListener("loadedmetadata", onMeta);
  };
  const scheduleRecovery = () => {
    const now = Date.now();
    if (now - retryRef.current.lastAt > 60000) retryRef.current.count = 0; // new incident
    retryRef.current.lastAt = now;
    if (retryRef.current.count < 3) {
      retryRef.current.count += 1;
      setRecovering(true);
      clearTimeout(retryRef.current.timer);
      retryRef.current.timer = setTimeout(reloadAndResume, 1200 * retryRef.current.count);
    } else {
      setRecovering(false);
      setDecodeFailed(true);
    }
  };
  const handleError = (e) => {
    const v = e.target;
    if (!v || !v.error) return;
    if (v.currentTime > 0.5) resumePosRef.current = v.currentTime;
    scheduleRecovery();
  };
  const manualRetry = () => {
    retryRef.current.count = 0;
    setDecodeFailed(false);
    setRecovering(true);
    reloadAndResume();
  };

  // Resume playback where the learner left off (never within the last 3s so `ended`
  // logic isn't skipped). Also advances the furthest-watched marker so the no-skip
  // lock honours what they already watched.
  // Android quirk: duration can still be unknown (NaN/Infinity) at loadedmetadata and
  // an early currentTime assignment may be silently dropped — so this retries across
  // loadedmetadata / durationchange / canplay / play until the seek actually lands.
  // CRITICAL: the initialTime prop GROWS as progress auto-saves every ~5s. It must be
  // frozen at mount — otherwise any later canplay/play (rebuffer, pause/resume, HLS
  // durationchange near the end) re-applies it and yanks playback BACK to the last
  // checkpoint (the "skips back a sentence near the end" bug).
  const initialSeekRef = useRef(initialTime);
  const seekVerifiedRef = useRef(false);
  const applyInitial = (v) => {
    const target = initialSeekRef.current;
    if (appliedInitialRef.current || !(target > 0)) return;
    // Playback already underway — the learner is past the point of an initial seek.
    if (v.currentTime > 1 && !v.paused) { appliedInitialRef.current = true; return; }
    const dur = v.duration;
    if (!dur || !isFinite(dur)) return; // metadata not ready yet — retry on a later event
    appliedInitialRef.current = true;
    if (target < dur - 3) {
      maxTimeRef.current = Math.max(maxTimeRef.current, target);
      try { v.currentTime = target; } catch { appliedInitialRef.current = false; }
    }
  };
  // On the first canplay, confirm the seek stuck; if Android dropped it, retry once.
  const verifyInitial = (e) => {
    const v = e.target;
    if (initialSeekRef.current > 1 && !seekVerifiedRef.current) {
      seekVerifiedRef.current = true;
      if (appliedInitialRef.current && v.paused && v.currentTime < 1) appliedInitialRef.current = false;
    }
    applyInitial(v);
  };

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => {}); else v.pause();
  };

  // Fire once per playthrough when the video truly reaches the end.
  const celebrate = () => {
    if (celebratedRef.current) return;
    celebratedRef.current = true;
    onWatched();
    onEnded && onEnded();
  };

  // Brief fade-to-completion moment (~0.9s in-and-out). Triggered ONLY by the real
  // `ended` event — never early — because the instructor talks right to the last frame.
  const [endFade, setEndFade] = useState(false);
  const endFadeTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(endFadeTimerRef.current), []);
  const runEndFade = () => {
    setEndFade(true);
    clearTimeout(endFadeTimerRef.current);
    endFadeTimerRef.current = setTimeout(() => setEndFade(false), 950);
  };

  const check = (e) => {
    const v = e.target;
    // Track the furthest point actually reached (robust even if duration resolves late).
    // With the no-skip lock on, don't advance the marker WHILE seeking, so a forward seek
    // can't inflate it (which would defeat the lock). Normal playback advances it freely.
    if (v.currentTime > maxTimeRef.current && (!lockForwardSeek || !v.seeking)) {
      maxTimeRef.current = v.currentTime;
    }
    if (v.currentTime > 0.5 && !v.seeking) resumePosRef.current = v.currentTime;
    const dur = v.duration;
    if (dur && isFinite(dur)) {
      const ratio = maxTimeRef.current / dur;
      // "Watched" is granted at the TRUE end only — the `ended` event, after playback
      // has stopped. Doing completion work while the last moments still play starved
      // the HLS decoder on phones (glitch/rewind near the end). Guard: if `ended`
      // never fires (rare hung players), a watchdog completes it a beat later.
      if (ratio >= 0.999 && !endGuardRef.current && !celebratedRef.current) {
        const remaining = Math.max(0, dur - v.currentTime);
        endGuardRef.current = setTimeout(celebrate, (remaining + 2) * 1000);
      }
    }
    // Persist playback position (throttled) so the learner never restarts from 0:00.
    if (onProgress && !v.seeking && v.currentTime > 1 && Math.abs(v.currentTime - lastSaveRef.current) >= 5) {
      lastSaveRef.current = v.currentTime;
      onProgress(Math.floor(v.currentTime));
    }
  };

  const onPlay = (e) => {
    setPlaying(true);
    setRecovering(false);
    applyInitial(e.target);
    // Reset the celebration guard when the learner restarts so replays re-fire.
    if (e.target.currentTime < 1) resetEndGuards();
  };
  // Forward-seek lock (students): allow rewinding freely, but snap back any attempt to skip
  // ahead of the furthest point actually watched. 1s pad keeps normal playback smooth.
  const SEEK_PAD = 1;
  const enforceNoSkip = (v) => {
    if (!lockForwardSeek || !v) return;
    // Final stretch: once the learner has genuinely reached the last ~10s, stop policing.
    // HLS gap-jumps over buffer holes in the final segment would otherwise get snapped
    // back ("skips back a sentence near the end" glitch). Not a cheat vector: the
    // exception only applies when the furthest-watched point is already near the end.
    const dur = v.duration;
    if (dur && isFinite(dur) && maxTimeRef.current >= dur - 10) return;
    if (v.currentTime > maxTimeRef.current + SEEK_PAD) v.currentTime = maxTimeRef.current;
  };
  const onSeeking = (e) => enforceNoSkip(e.target);
  const onSeeked = (e) => {
    if (e.target.currentTime < 1) resetEndGuards();
    enforceNoSkip(e.target);
  };

  // Maximize: native fullscreen when the browser supports it (hides ALL browser chrome,
  // survives rotation). CSS visual-viewport overlay remains as the iOS/unsupported fallback.
  const [maximized, setMaximized] = useState(false);
  const [nativeFs, setNativeFs] = useState(false);
  const containerRef = useRef(null);
  const nativeTriedRef = useRef(false);
  const setMax = (on) => {
    setMaximized(on);
    const el = containerRef.current;
    if (on) {
      const rf = el && (el.requestFullscreen || el.webkitRequestFullscreen);
      if (rf) {
        nativeTriedRef.current = true;
        try {
          const p = rf.call(el, { navigationUI: "hide" });
          if (p && p.catch) p.catch(() => { nativeTriedRef.current = false; });
        } catch { nativeTriedRef.current = false; }
      }
    } else {
      const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
      if (fsEl) {
        try { (document.exitFullscreen || document.webkitExitFullscreen).call(document); } catch {}
      }
    }
  };
  useEffect(() => {
    const onFsChange = () => {
      const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
      setNativeFs(fsEl === containerRef.current);
      // System gesture / Esc exited native fullscreen → exit max mode entirely.
      if (!fsEl && nativeTriedRef.current) { nativeTriedRef.current = false; setMaximized(false); }
    };
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("webkitfullscreenchange", onFsChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("webkitfullscreenchange", onFsChange);
    };
  }, []);
  useEffect(() => {
    if (!maximized) return;
    const onKey = (e) => { if (e.key === "Escape") setMax(false); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maximized]);

  return (
    <div data-testid={`video-block-${index}`}>
      {total > 1 && (
        <div className="text-xs uppercase tracking-[0.15em] text-[#000000] mb-1 flex items-center gap-2">
          <span>Part {index + 1}{video.label ? ` · ${video.label}` : ""} <span className="text-[#000000]">({ar})</span></span>
          {watched && (
            <span className="normal-case tracking-normal px-1.5 py-0.5 rounded-full bg-[#CCFF00]/25 text-[#7a8a00] font-display font-bold flex items-center gap-1" data-testid={`video-part-watched-${index}`}>
              <CheckCircle size={11} weight="fill" /> Watched
            </span>
          )}
        </div>
      )}
      <div
        ref={containerRef}
        className={maximized
          ? "fixed z-[130] bg-black"
          : "relative bg-black rounded-2xl overflow-hidden mx-auto"}
        style={maximized
          // Native fullscreen: the browser sizes the element to the screen. Fallback
          // overlay pins to the VISUAL viewport (--vv* from lib/viewport.js).
          ? (nativeFs
            ? { top: 0, left: 0, width: "100%", height: "100%" }
            : { top: "var(--vvt, 0px)", left: "var(--vvl, 0px)", width: "var(--vvw, 100vw)", height: "var(--vvh, 100dvh)" })
          : { aspectRatio: `${ratio}`, width: `min(100%, calc(${maxVh}vh * ${ratio}))`, margin: "0 auto" }}
      >
        {isEmbed ? (
          <iframe
            src={url} title={video.label || `video-${index}`} className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen data-testid={`lesson-iframe-${index}`}
          />
        ) : (
          <>
            <video
              ref={videoRef}
              src={isBunny ? undefined : url} controls controlsList="nodownload nofullscreen noremoteplayback" preload="metadata"
              disablePictureInPicture disableRemotePlayback
              onContextMenu={(e) => e.preventDefault()}
              poster={video.thumbnail_url || undefined}
              onLoadedMetadata={(e) => {
                const v = e.target;
                if (v.videoWidth && v.videoHeight) setRealRatio(v.videoWidth / v.videoHeight);
                applyInitial(v);
              }}
              onTimeUpdate={check} onDurationChange={(e) => { applyInitial(e.target); check(e); }}
              onCanPlay={verifyInitial} onEnded={(e) => { runEndFade(); celebrate(e); }}
              onPlay={onPlay}
              onPause={(e) => { setPlaying(false); if (onProgress && e.target.currentTime > 1 && !e.target.ended) { lastSaveRef.current = e.target.currentTime; onProgress(Math.floor(e.target.currentTime)); } }}
              onSeeking={onSeeking} onSeeked={onSeeked}
              onError={handleError}
              className="w-full h-full object-contain bg-black no-native-fs" data-testid={`lesson-video-${index}`}
            >
              {ccUrls.en && <track kind="subtitles" srcLang="en" label="English" src={ccUrls.en} />}
              {ccUrls.fr && <track kind="subtitles" srcLang="fr" label="Français" src={ccUrls.fr} />}
            </video>
            <button type="button"
              className={"absolute z-20 grid place-items-center rounded-full bg-black/55 text-white border border-white/25 cursor-pointer " +
                (maximized ? "w-11 h-11" : "w-9 h-9 top-2 right-2")}
              style={maximized ? { top: "calc(env(safe-area-inset-top, 0px) + 10px)", right: "12px" } : undefined}
              onClick={() => setMax(!maximized)}
              title={maximized ? "Exit full screen" : "Full screen"}
              aria-label={maximized ? "Exit full screen" : "Full screen"}
              data-testid={`video-maximize-${index}`}>
              {maximized ? <CornersIn size={22} weight="bold" /> : <CornersOut size={18} weight="bold" />}
            </button>
            {endFade && (
              <div className="absolute inset-0 z-10 bg-black pointer-events-none animate-end-fade" data-testid={`video-end-fade-${index}`} />
            )}
            {isBunny && (bunnyState === "processing" || bunnyState === "loading") && (
              <div className="absolute inset-0 z-10 grid place-items-center bg-black/80" data-testid={`video-processing-${index}`}>
                <div className="text-white/85 text-sm flex items-center gap-2 px-6 text-center">
                  <CircleNotch size={18} className="animate-spin shrink-0" />
                  {bunnyState === "loading" ? "Loading video…" : "This video is still being prepared — it will appear here automatically."}
                </div>
              </div>
            )}
            {recovering && !decodeFailed && (
              <div className="absolute inset-0 z-10 grid place-items-center bg-black/60 pointer-events-none" data-testid={`video-reconnecting-${index}`}>
                <div className="text-white/90 text-sm flex items-center gap-2">
                  <CircleNotch size={18} className="animate-spin" /> Reconnecting…
                </div>
              </div>
            )}
            {decodeFailed && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-black/90 p-6 text-center" data-testid={`video-error-${index}`}>
                <p className="text-white/85 text-sm max-w-sm">The connection dropped while playing this video. Check your signal and tap retry — it will pick up where it stopped.</p>
                <button type="button" className="btn-primary text-sm flex items-center gap-1.5" onClick={manualRetry} data-testid={`video-retry-${index}`}>
                  <ArrowClockwise size={16} weight="bold" /> Tap to retry
                </button>
              </div>
            )}
            {/* Click anywhere on the video to play/pause — pointer devices only. On
                touch phones the native controls already have a big play button. */}
            {!IS_TOUCH && (
              <>
                <div
                  className="absolute inset-x-0 top-0 bottom-14 cursor-pointer"
                  onClick={togglePlay}
                  data-testid={`video-click-layer-${index}`}
                />
                {!playing && (
                  <div className="absolute inset-0 grid place-items-center pointer-events-none">
                    <motion.div
                      initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                      transition={{ type: "spring", stiffness: 320, damping: 20 }}
                      className="w-20 h-20 rounded-full grid place-items-center bg-black/55 backdrop-blur-sm border border-white/25 shadow-xl"
                      data-testid={`video-big-play-${index}`}
                    >
                      <Play size={40} weight="fill" color="#CCFF00" className="ml-1" />
                    </motion.div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
      {!isEmbed && lessonId && video.id && (
        <div className="flex items-center gap-1.5 mt-1.5 flex-wrap" data-testid={`video-cc-controls-${index}`}>
          <span className="text-[11px] font-bold text-[#52525B] mr-0.5">Subtitles:</span>
          {[["off", "Off"], ["en", "English"], ["fr", "Français"]].map(([l, lbl]) => (
            <button key={l} type="button" onClick={() => enableCc(l)} disabled={ccBusy !== null}
              data-testid={`video-cc-${l}-${index}`}
              style={{
                padding: "2px 10px", borderRadius: "999px", fontSize: "11px", fontWeight: 700,
                cursor: "pointer", background: cc === l ? "#0A0D14" : "#FFFFFF",
                color: cc === l ? "#FFFFFF" : "#52525B",
                border: "1px solid " + (cc === l ? "#0A0D14" : "#C4C8CE"),
              }}>
              {ccBusy === l ? (l === "fr" ? "Préparation…" : "Preparing…") : lbl}
            </button>
          ))}
          {ccErr && <span className="text-[11px] text-[#b45309]" data-testid={`video-cc-error-${index}`}>{ccErr}</span>}
          <span className="text-[11px] font-bold text-[#52525B] ml-2 mr-0.5">Audio:</span>
          {[["off", "Original"], ["fr", "Voix FR"]].map(([l, lbl]) => (
            <button key={l} type="button" onClick={() => enableVoice(l === "fr")}
              data-testid={`video-voice-${l}-${index}`}
              style={{
                padding: "2px 10px", borderRadius: "999px", fontSize: "11px", fontWeight: 700,
                cursor: "pointer", background: voice === l ? "#0A0D14" : "#FFFFFF",
                color: voice === l ? "#FFFFFF" : "#52525B",
                border: "1px solid " + (voice === l ? "#0A0D14" : "#C4C8CE"),
              }}>
              {lbl}
            </button>
          ))}
          {voice === "fr" && voiceState === "loading" && (
            <span className="text-[11px] text-[#52525B]" data-testid={`video-voice-loading-${index}`}>Voix française en préparation… elle démarrera automatiquement.</span>
          )}
          {voice === "fr" && voiceState === "error" && (
            <span className="text-[11px] text-[#b45309]" data-testid={`video-voice-error-${index}`}>Voix française indisponible pour cette vidéo.</span>
          )}
          {voiceUrl && <audio ref={audioRef} src={voiceUrl} preload="auto" data-testid={`video-voice-audio-${index}`} />}
        </div>
      )}
      {isEmbed && (
        <button data-testid={`mark-watched-${index}`} className="btn-secondary text-xs mt-1.5" onClick={celebrate}>
          I've watched this
        </button>
      )}
    </div>
  );
}

function RewardsBar() {
  const { user } = useAuth();
  if (!user) return null;
  const today = user.coins_earned_today ?? 0;
  return (
    <div data-testid="rewards-bar" className="rounded-2xl px-4 py-3 mt-3 flex items-center gap-4 flex-wrap" style={{ background: "#0A0D14" }}>
      <span className="text-2xl" aria-hidden>🪙</span>
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#94A3B8]">Your coins</div>
        <div className="font-display font-extrabold text-2xl leading-none" style={{ color: "#F5B301" }} data-testid="rewards-total">
          {(user.coins_balance ?? 0).toLocaleString()}
        </div>
      </div>
      {today > 0 && (
        <span className="chip font-display font-bold text-sm" style={{ background: "linear-gradient(180deg,#FFE066,#F5B301)", color: "#4A3200", border: "1px solid #E0A800" }} data-testid="rewards-today">
          +{today.toLocaleString()} today
        </span>
      )}
      <span className="text-xs text-[#94A3B8] ml-auto hidden sm:block">Coins double with every lesson you finish today</span>
    </div>
  );
}

function QuizGate({ ready, videoWatched, docsOpened, totalDocs, quizMeta, onStart, completed }) {
  const { t } = useI18n();
  return (
    <div data-testid="quiz-gate">
      <div className="flex items-center gap-2 mb-2">
        <ShieldCheck size={20} weight="duotone" color={ready ? "#CCFF00" : "#52525B"} />
        <h3 className="font-display font-bold text-lg">QUIZ</h3>
      </div>
      <ul className="text-sm text-[#000000] mb-3 space-y-1">
        <li className="flex items-center gap-2">
          {videoWatched ? <CheckCircle size={14} weight="fill" color="#CCFF00" className="icon-outline" /> : <div className="w-3.5 h-3.5 rounded-full border border-[#A1A1AA]" />}
          {t("Video watched")}
        </li>
        {totalDocs > 0 && (
          <li className="flex items-center gap-2">
            {docsOpened ? <CheckCircle size={14} weight="fill" color="#CCFF00" className="icon-outline" /> : <div className="w-3.5 h-3.5 rounded-full border border-[#A1A1AA]" />}
            {t("All resources opened")}
          </li>
        )}
      </ul>
      {completed && <div className="text-[#166534] text-sm font-display mb-2">{t("Already done — you can take it again any time.")}</div>}
      <button data-testid="quiz-start-button" className="btn-primary" disabled={!ready} onClick={onStart}>
        {ready ? t("Start quiz") : t("Locked")}
      </button>
    </div>
  );
}

function NoFailQuiz({ questions, onFinish, submitting, allQuestions, initialIdx = 0, initialAnswers = {}, onAutosave }) {
  const { t } = useI18n();
  const [idx, setIdx] = useState(Math.min(initialIdx, questions.length - 1));
  const [answers, setAnswers] = useState(initialAnswers);
  const [selected, setSelected] = useState(null);
  const [revealed, setRevealed] = useState(false);

  const q = questions[idx];
  const isLast = idx === questions.length - 1;
  const correct = q.correct_index;
  const wasRight = selected === correct;

  const choose = (oi) => {
    if (revealed) return;
    setSelected(oi);
    setRevealed(true);
    setAnswers(a => ({ ...a, [q.id]: oi }));
  };

  const next = () => {
    if (isLast) { onFinish({ ...answers }); return; }
    onAutosave && onAutosave(idx + 1, { ...answers });
    setIdx(i => i + 1); setSelected(null); setRevealed(false);
  };

  return (
    <div data-testid="nofail-quiz">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-bold text-lg flex items-center gap-2">
          {t("Quick check")}
          {allQuestions && (
            <span data-testid="all-questions-badge" className="text-[10px] font-sans font-bold uppercase tracking-wide px-2 py-0.5 rounded-full" style={{background:"#0A0D14",color:"#CCFF00"}} title="Super Admin function check: the full question pool is shown">All questions · admin check</span>
          )}
        </h3>
        <span className="text-xs text-[#000000]">{t("Question")} {idx + 1} {t("of")} {questions.length}</span>
      </div>
      <div className="p-4 rounded-xl border border-[#C4C8CE] bg-[#FFFFFF]" data-testid={`nofail-question-${idx}`}>
        <div className="font-bold text-black mb-3" data-testid="nofail-question-text">{q.question}</div>
        <div className="flex flex-col gap-1.5">
          {q.options.map((opt, oi) => {
            let border = "#94979C", bg = "transparent", icon = null;
            if (revealed) {
              if (oi === correct) { border = "#4d7c0f"; bg = "rgba(204,255,0,0.28)"; icon = <Check size={16} weight="bold" color="#3f6212" className="shrink-0" />; }
              else if (oi === selected) { border = "#dc2626"; bg = "rgba(220,38,38,0.12)"; icon = <X size={16} weight="bold" color="#b91c1c" className="shrink-0" />; }
            } else if (oi === selected) { border = "#8fb300"; bg = "rgba(204,255,0,0.18)"; }
            return (
              <button type="button" key={oi}
                data-testid={`nofail-option-${idx}-${oi}`}
                onClick={() => choose(oi)}
                disabled={revealed}
                className="text-left px-3 py-2 rounded-lg border text-black font-normal flex items-center justify-between gap-2"
                style={{ borderColor: border, background: bg, cursor: revealed ? "default" : "pointer" }}>
                <span>{opt}</span>{icon}
              </button>
            );
          })}
        </div>

        {revealed && (
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="mt-4" data-testid="nofail-reveal">
            <div className="font-display font-bold text-lg" style={{ color: wasRight ? "#3f6212" : "#c2410c" }}>
              {wasRight ? t("Nice — that's right!") : t("Don't give up — everybody makes mistakes!")}
            </div>
            {!wasRight && (
              <div className="text-sm text-black mt-1">
                {t("The correct answer is:")} <span className="font-bold">{q.options[correct]}</span>
              </div>
            )}
            {q.correct_answer_explanation && (
              <div className="text-sm text-[#000000] mt-1">{q.correct_answer_explanation}</div>
            )}
          </motion.div>
        )}
      </div>

      <button
        data-testid="nofail-next-button"
        className="btn-primary mt-4 flex items-center gap-2"
        onClick={next}
        disabled={!revealed || submitting}
      >
        {submitting ? t("Saving...") : isLast ? t("Finish") : t("Next question")}
        <ArrowRight size={16} weight="bold" />
      </button>
    </div>
  );
}

function ActiveQuiz({ quizState, setAnswer, onSubmit, submitting }) {
  const { t } = useI18n();
  return (
    <div data-testid="active-quiz">
      <h3 className="font-display font-bold text-lg mb-3">Quiz</h3>
      <div className="flex flex-col gap-4">
        {quizState.questions.map((q, qi) => (
          <div key={q.id} className="p-3 rounded-xl border border-[#C4C8CE] bg-[#FFFFFF]" data-testid={`quiz-question-${qi}`}>
            <div className="font-bold text-black mb-2" data-testid={`quiz-question-text-${qi}`}>{qi+1}. {q.question}</div>
            <div className="flex flex-col gap-1.5">
              {q.options.map((opt, oi) => {
                const sel = quizState.answers[q.id] === oi;
                return (
                  <button type="button" key={oi}
                    data-testid={`quiz-option-${qi}-${oi}`}
                    onClick={() => setAnswer(q.id, oi)}
                    className="text-left px-3 py-2 rounded-lg border bg-transparent cursor-pointer text-black font-normal"
                    style={{ borderColor: sel ? "#8fb300" : "#94979C", background: sel ? "rgba(204,255,0,0.18)" : "transparent" }}>
                    {opt}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <button data-testid="quiz-submit-button" className="btn-primary mt-4" onClick={() => onSubmit()} disabled={submitting}>
        {submitting ? t("Submitting...") : t("Submit answers")}
      </button>
    </div>
  );
}

function QuizResult({ result, threshold, onRetry, onContinue, canAdvance, celebrationsEnabled = true, hasPrev = false, onPrev, videoWatched = true, reveal = null, onComeBackLater, submitting = false, completedCount = 0, onCelebDone }) {
  const { t } = useI18n();
  if (result.passed) {
    return (
      <LessonCelebration
        completedCount={completedCount}
        coinsAwarded={result.coins_awarded ?? result.xp_awarded ?? 0}
        totalCoins={result.coins_balance ?? result.xp ?? 0}
        scorePercent={result.score_percent}
        correctCount={result.correct_count}
        totalCount={result.total_count}
        canAdvance={canAdvance}
        onRetry={onRetry}
        onContinue={onContinue}
        hasPrev={hasPrev}
        onPrev={onPrev}
        onDone={onCelebDone}
        soundOn={celebrationsEnabled}
      />
    );
  }

  // "Come back later" answer reveal (school students, teachable-moment).
  if (reveal) {
    return (
      <div data-testid="quiz-reveal">
        <div className="text-xl font-extrabold font-display text-black">No worries — here are the answers</div>
        <div className="text-sm text-[#000000] mb-3">Review these, then come back and try again anytime — you'll get fresh questions.</div>
        <div className="flex flex-col gap-3">
          {reveal.map((q, qi) => (
            <div key={q.id} className="p-3 rounded-xl border border-[#C4C8CE] bg-white" data-testid={`reveal-question-${qi}`}>
              <div className="font-bold text-black mb-1">{qi + 1}. {q.question}</div>
              <div className="text-[#166534] font-semibold">✓ {q.options[q.correct_index]}</div>
              {q.correct_answer_explanation && <div className="text-sm text-[#000000] mt-1">{q.correct_answer_explanation}</div>}
            </div>
          ))}
        </div>
        <button data-testid="reveal-continue-button" className="btn-primary mt-4" onClick={onContinue}>Continue</button>
      </div>
    );
  }

  const rewatch = result.rewatch_required;
  return (
    <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
      transition={{ type: "spring", stiffness: 280, damping: 22 }}
      className="text-center py-4" data-testid="quiz-result">
      <div className="text-6xl font-extrabold font-display" style={{ color: "#FF5100" }}>{result.score_percent}%</div>
      <div className="text-2xl font-extrabold font-display mt-1 text-black">
        {rewatch ? "Let's watch it once more" : `Below ${threshold}% — give it another shot`}
      </div>
      <div className="text-[#000000] mt-1">{result.correct_count} / {result.total_count} correct</div>
      {rewatch && (
        <div className="text-[#000000] mt-2 text-sm" data-testid="rewatch-notice">
          {videoWatched ? "Nice — you rewatched it. Ready to try again?" : "Scroll up and watch the video again to unlock a retry."}
        </div>
      )}
      <div className="flex items-center justify-center gap-2 mt-5 flex-wrap">
        <button data-testid="quiz-retry-button" className="btn-secondary" disabled={submitting || (rewatch && !videoWatched)} onClick={onRetry}>
          {t("Retry quiz")}
        </button>
        {rewatch && (
          <button data-testid="come-back-later-button" className="btn-ghost border border-[#C4C8CE] rounded-xl px-4" disabled={submitting} onClick={onComeBackLater}>
            {t("Come back later")}
          </button>
        )}
      </div>
    </motion.div>
  );
}
